/* Dynamic Plot code copyright 2015-2022 (c) Tuva Labs Inc. */
/**
 * Reference Line plot elements: XRefLineElement, YRefLineElement, DiagRefLineElement
 * Added to Dyn.PlotShaper's list of plot elements, to make a plot.
 * created by jpatel and cmiller
 */

import ContextMenuUtil from '../../../utils/ContextMenuUtil';
import { PlotElement} from './plotElement'

export const wrapDynRefLineElement = function(Dyn) {
//let mediator = Dyn.mediator;

class RefLineModel {
    constructor( nCoord, aCoord, lineColor ) {
        this.coord = new Dyn.axisUtil.NACoord( nCoord, aCoord );
        this.color = lineColor || "black";
    }

    save() {
        var data = {};
        data.coord = this.coord.save();
        data.color = this.color;
        return data;
    }

    restore( data ) {
        if( data.coord ){
            this.coord = Dyn.axisUtil.NACoord.restore( data.coord );
        }
        if( data.color ){
            this.color = data.color;
        }
    }
}
Dyn.RefLineModel = RefLineModel;

//============================

class RefLineBaseElement extends PlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.lines = []; // array of line locations
        this.selectedLineIndex = -1; // index into this.lines or -1 if nothing selected
        // state of Y2/Dual axis
        this.isDualAxisOn = false; // may be true or false for Elements controlling lines on Y1 and Y2 axes (false on X)
        this.hasNumericY2Axis = false; // may be true or false for lines on Y1 and Y2 axes, always false for X axis lines
        // this.isOnY2 = false; // true only if Element controls Ref. Line on Y2, or Movable Line on X+Y2
    }

    get isOnY2() { return false; } /* subclasses can override */

    /** {OVERRIDE} upon save keep track of the line positions */
    save() {
        // get PlotElement base model
        let plotElementModel = super.save();

        // add the lines to the model
        plotElementModel.lines = [];
        this.lines.forEach( refLineModel =>
            plotElementModel.lines.push( refLineModel.save()));
        if( this.isDiagonal && this.showSquaresLineIndex >= 0 ) {
            plotElementModel.showSquaresLineIndex = this.showSquaresLineIndex;
        }

        return plotElementModel;
    }

    /** {OVERRIDE} Restore the plot, and restore the line positions */
    restore( savedData ) {
        // restore base model
        super.restore( savedData );

        // restore the lines
        this.lines = [];
        if( savedData.lines ) {
            savedData.lines.forEach( value => {
                if( value ) {
                    let lineModel = this.isDiagonal ?
                            new Dyn.DiagRefLineModel() :
                            new Dyn.RefLineModel();
                    lineModel.restore( value );
                    this.lines.push( lineModel );
                }
            } );
        }
        if( this.isDiagonal ) {
            this.showSquaresLineIndex = Number.isFinite(savedData.showSquaresLineIndex) ? savedData.showSquaresLineIndex : -1;
        }
        this.isNewlyRestoredData = true; // to prevent calcAxis() from stripping attribute coordinates (issue #555)
    }

    /**
     * Restore the plot element during a SwapAxis, to swap the lines from one axis to another
     * Example: new Dyn.XRefLineElement( yRefLineElement.save() ) to move the Y lines to X.
     * @param savedData
     */
    swapAxis( savedData ) {
        "use strict";
        // restore the line positions
        if( ! this.isDiagonal ) {
            this.restore( savedData ); // restore X line data to Y, or Y line data to X, as part of swap.
        }
    }

    /** override default undo action */
    getUndoAction( contextHints ) {
        // to get "Undo Add Ref. Line on Y2" for contextHints.action==="addElement"
        // to get "Undo Remove Ref. Line on Y2 for contextHints.action==="removeElement"
        // or get "Undo Move Rectangle Annotation for others
        const name = Dyn.loc( this.isDiagonal ? "Movable Line on {{axis}}" : "Ref. Line on {{axis}}", { axis:this.kAxisName });
        if( contextHints?.action==="addElement") {
            return Dyn.loc( "Add {{action}}", { action:name });
        }
        return name;
    }

    /**
     * Return true if this element has all toggle elements turned off, and thus can be removed from the plot.
     * @returns {boolean}
     */
    isElementWithAllTogglesOff() {
        "use strict";
        return( this.lines.length === 0 );
    }

    // modify the control type in this plot element, to turn on and off features
    toggleControl( toggleControlID ) {
        "use strict";
        // instead of toggling anything just add another reference line
        this.addOneLine();
    }

    getCloseBox( adornments ) {
        "use strict";
        if( adornments.length >= this.kNumInitialAdornments ) {
            return adornments[ 0 ];
        }
        return null;
    }

    _getContextMenuButton( adornments ) {
        "use strict";
        if( adornments.length >= this.kNumInitialAdornments ) {
            return adornments[ 1 ];
        }
        return null;
    }

    getLineAdornment( adornments, lineIndex, partIndex ) {
        "use strict";
        var adornmentIndex = this.kNumInitialAdornments +( lineIndex * this.kNumAdornmentsPerLine ) + (partIndex ? partIndex : 0);
        if( adornments.length >= adornmentIndex ) {
            return adornments[ adornmentIndex ];
        }
        return null;
    }

    /**
     * Update the color of the line, to match the setting in the color model.
     * Note: derived class DiagRefLineElement overrides.
     * @param lineIndex
     * @param newColor
     * @param [isTemporaryChange]
     * @private
     */
    setLineElementColor( lineIndex, newColor, isTemporaryChange ) {
        "use strict";
        var lineAdorns = this.getAdornmentsArray(),
            lineModel = this.lines[lineIndex],
            lineElement = this.getLineAdornment( lineAdorns, lineIndex, 0 ), //set by addRefLineElement()
            textElement = this.getLineAdornment( lineAdorns, lineIndex, 1 );
        Dyn.assert( ! this.isDiagonal ); // DiagRefLineElement() should override since it has different elements

        if( ! isTemporaryChange ){
            lineModel.color = newColor;
        }
        lineElement.attr( {stroke: lineModel.color} ); // line color
        textElement.attr( {fill: lineModel.color} ); // text color
    }

    /**
     * Add  one reference line location. calcAdornment() will build line elements to match.
     * updates this.lines[], this.selectedLineIndex
     * @private
     */
    addOneLine() {
        "use strict";

        var normalizedLocation = this.firstRefLineLocation,
            numExistingLines = this.lines.length;
        if( numExistingLines > 0 ) {
            // lines object has NAcoord, so use n element to get next position
            normalizedLocation = this.lines[numExistingLines - 1].coord.n + this.nextRefLineLocation;
            normalizedLocation -= Math.floor( normalizedLocation );
        }

        var newLineModel = new Dyn.RefLineModel( normalizedLocation, undefined, this.kLineColors[0] );
        this.lines.push( newLineModel );      // add a new line position
        if( ! this.plotShaper ){
            this.deferredSelectLine = numExistingLines; // select later with handleDeferredSelectLine()
        } else {
            delete this.deferredSelectLine; // remove old deferred selection if any
            this.selectAdornment( numExistingLines );
        }
        // calcAdornments() will add line adornment and show the line as selected (with close box)
    }

    /**
     * Remove the given Reference Line or Movable Line; if the last line; remove this entire plot element.
     * @param lineIndex
     */
    removeLineAndAdornment( lineIndex ) {
        let isValid = ( lineIndex >= 0 && lineIndex < this.lines.length );
        Dyn.assert( isValid, "Invalid line index:"+lineIndex+" of "+this.lines.length );
        if( lineIndex === 0 && this.lines.length === 1 ) {
            // remove the only line and this plot element, with undo
            Dyn.mediator.publish( "plot:removePlotElement", { controlID: this.primaryType } );
            // NOTE: this.plotShaper === null from this point on
        } else if ( isValid ) {
            // remove one line; keep the rest
            let adornmentArray = this.getAdornmentsArray(),
                adornmentIndex = this.kNumInitialAdornments +( lineIndex * this.kNumAdornmentsPerLine ),
                redrawSquares = false,
                saveData = this.save();

            // remove line and its graphical elements
            let removedElements = adornmentArray.splice( adornmentIndex, this.kNumAdornmentsPerLine );
            removedElements.forEach( e => e.remove()); // remove from paper
            this.lines.splice( lineIndex, 1 );

            // update line index of remaining line adornments
            this.lines.forEach((line,lineIndex) => {
                for( let partIndex=0; partIndex<this.kNumAdornmentsPerLine; partIndex++ ) {
                    let lineElement = this.getLineAdornment( adornmentArray, lineIndex, partIndex );
                    if( Number.isFinite( lineElement?.data("lineIndex"))) {
                        lineElement.data( "lineIndex", lineIndex );
                    }
                }
            });

            // clear selection of this line
            if( this.selectedLineIndex === lineIndex ) {
                this.clearSelectedAdornments();
            }
            if( this.showSquaresLineIndex === lineIndex ) {
                // if removing the line that is showing squares, find a new line to show squares, if any,
                // on this element, else on another element, if any. Indirectly calls this.setShowSquares()
                this.plotShaper.showHideSquaresOnLines( this.plotShaper.showSquaresOnLines,
                    (this.lines.length > 0) ? this : null ); // use this line if any more lines left
                redrawSquares = true;
            }
            this.removeGlow();
            Dyn.assert( this.lines.length >= 1 );

            // save changes with plot for undo redo; redraw other adornments if necessary
            Dyn.mediator.publish( "plot:updateAdornment", {
                saveData: saveData, // to create an undo step
                restoreData: this.save(), // save changed lines for redo
                actionString: Dyn.loc( "Remove {{action}}", { action: this.getUndoAction() }),
                updatePlot: redrawSquares, // redraw adornments to show change in squares?
            } );
        }
    }

    /**
     * Make a close box for the reference line.
     * Caller must position the attribute.
     * @returns {*}
     */
    makeCloseBox( ) {
        "use strict";

        function buttonHoverIn( e ) {
            cover.attr({ fill: Dyn.plotUtil.axisButtonBgDownColor });
            this.attr( { fill: Dyn.plotUtil.axisButtonDownColor, stroke: "none" } );
            if( this.data("tooltip"))
                Dyn.toolTip.show( this, event, { x:this.getBBox().x2, y:this.getBBox().y2 });
        }
        function buttonHoverOut( e ) {
            cover.attr({ fill: Dyn.plotUtil.axisButtonBgUpColor });
            this.attr( { fill: Dyn.plotUtil.axisButtonUpColor, stroke: "none" } );
            if( Dyn.toolTip.isMyElement( this ))
                Dyn.toolTip.hide();
        }
        function clickedOnClosebox( e ) {
            // handler to remove line when clicking on X
            // assumes closebox is only shown when a line is selected
            plotElement.removeLineAndAdornment( plotElement.selectedLineIndex );
            e.stopPropagation(); // prevents event going to leaflet/map layer
            e.preventDefault();
        }

        var plotElement = this,
            closebox = Dyn.plotUtil.makeCloseBox(
                this.plotShaper.adornments.paper, buttonHoverIn, buttonHoverOut, clickedOnClosebox )
                .data("tooltip", Dyn.loc( this.isDiagonal ? 'remove movable line' : 'remove reference line')),
            cover = closebox.getCoverElement();
        this.plotShaper.setAdornmentLayer( 2, closebox );
        return closebox;
    }

    /**
     * Make a color selector for the currently selected reference line.
     * Caller must position the selector.
     * @returns {*} -- Raphael color selector element
     */
    makeContextMenuButton( ) {

        function buttonClick( e ) {
            // handler to pick line color, called by click or keydown event
            var lineIndex = plotElement.selectedLineIndex,
                symbolElement = this,
                coverElement = buttonSet.getCoverElement();

            if( lineIndex >= 0 && lineIndex < plotElement.lines.length ) {
                var lineModel = plotElement.lines[lineIndex],
                    xy = Dyn.plotUtil.clickOrKeyboardEventToPaperYX( e, symbolElement ),
                    isDiagonal = (lineModel.displayName === 'DiagRefLineModel');
                setTimeout(() => { // delayed call is required to show when e.type==='keykdown', reason unknown
                    ContextMenuUtil.show( {x:xy.x, y:xy.y}, { colorHex:lineModel.color, colorsArray:plotElement.kLineColors, isShowSquaresOnLinesEnable: isDiagonal }
                        , ( color ) => {
                            if( color && color.hex ){
                                Dyn.assert(typeof color.hex === 'string');
                                plotElement.setLineElementColor(lineIndex, color.hex);
                                Dyn.mediator.publish("plot:updateAdornment", {}); // to update color in Summary View
                                Dyn.plotUtil.setAsKeyboardFocus(coverElement);
                            }
                        },  isDiagonal ? Dyn.plotUtil.refDiagContextMenuID : Dyn.plotUtil.refLineContextMenuID, Dyn);
                }, 100 );
            }
        }
        function deleteHandler() {
            // note: color selector will only be visible if there is a selected line
            plotElement._removeLineAndAdornment( plotElement.selectedLineIndex );
        }

        var plotElement = this,
            tooltipAndAriaLabel = Dyn.loc( this.isDiagonal ? "movable line menu" : "reference line menu" ),
            buttonSet = Dyn.plotUtil.makeContextMenuButton(
                this.plotShaper.adornments.paper,
                buttonClick,
                { deleteHandler:deleteHandler } ,
                tooltipAndAriaLabel,
                this.isDiagonal ? Dyn.plotUtil.refDiagContextMenuID : Dyn.plotUtil.refLineContextMenuID)
        this.plotShaper.setAdornmentLayer( 2, buttonSet );
        return buttonSet;
    };

    /**
     * {OVERRIDE} Deselect any selected adornments in this plot element.
     */
    clearSelectedAdornments() {
        let oldSelection = this.selectedLineIndex;
        this.selectedLineIndex = -1;
        this.updateSelectionVisuals();
        return( oldSelection !== this.selectedLineIndex );
    };

    /**
     * Select an adornment.  We only allow selection of the lines, not closeboxes.
     * See updateSelectionVisuals() for the visual indicator of a selected line.
     * @param adornmentIndex - if defined, the index of the adornment to select
     * @param [data] - if defined, the IDs of the line to select
     */
    selectAdornment( adornmentIndex, data ) {
        let oldSelection = this.selectedLineIndex,
            lineIndex = adornmentIndex;
        if( data ) {
            if ( ["referenceLine","movableLine"].includes( data.tableSetID )) {
                // select the reference line or movable line that matches the summary view row's select ID (e.g. 'Y-3')
                lineIndex = this.lines.findIndex( (line,index) => this.getLineID(index)===data.lineID);
            }
        }
        if( Dyn.mathUtil.isInIntegerRange( lineIndex, 0, this.lines.length )) {
            // record the selected line
            this.selectedLineIndex = lineIndex;
            if( this.isDiagonal ){
                // update show squares status to match selected line; caller must redraw squares
                if( this.plotShaper.showSquaresOnLines ){
                    this.plotShaper.showHideSquaresOnLines( true, this ); // turn on for this element, off for others
                } else {
                    this.showSquaresLineIndex = -1;
                }
            }
            this.plotShaper.clearSelectedAdornments( this ); // clear other adornment selections
        } else {
            this.clearSelectedAdornments();
        }
        return( oldSelection !== this.selectedLineIndex );
    };

    /** Does this element have a selected line with the given ID?  */
    isAdornmentSelected( tableSetID, adornmentID ) {
        return(
            ["referenceLine","movableLine"].includes( tableSetID )
            && (this.selectedLineIndex >= 0 )
            && (this.getLineID( this.selectedLineIndex ) === adornmentID ));
    }

    /**
     * Check to see if a new line was selected during initialization, and if so,
     * handle the selection and update now, now that object is fully initialized.
     * @return {boolean}
     */
    handleDeferredSelectLine() {
        let handled = false;
        if( this.deferredSelectLine >= 0 ) {
            this.selectAdornment( this.deferredSelectLine );
            delete this.deferredSelectLine;
            handled = true; // caller should call updateSelectionVisuals(), now may not be the right time.
        }
        return handled;
    }

    /**
     * Update the position of the visual elements that show the selection.
     * This includes the closebox and context menu icons. See selectAdornment().
     */
    updateSelectionVisuals() {
        "use strict";

        function timesIconSize( scale ){ // e.g. 2 => return 2 * 20;
            return scale * (iconSize + Dyn.plotUtil.plotButtonSpacing );
        }

        let refLineAdorns = this.getAdornmentsArray(),
            lineIndex = this.selectedLineIndex,
            wantVisible = !( Dyn.preferences.isViewOnly() || (this.isOnY2 && ! this.isDualAxisOn)),
            iconSize = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize ),
            closeBox = this.getCloseBox( refLineAdorns ),
            contextMenu = this._getContextMenuButton( refLineAdorns );

        if( lineIndex >= 0 && lineIndex < this.lines.length ) {
            let lineElement = this.getLineAdornment( refLineAdorns, lineIndex, 0 ),
                expandedClip = iconSize * 0.3, // expand clip area a little to not clip context menu
                clipRectString = Dyn.plotView.getPlotInteriorClipRect( expandedClip, expandedClip ),
                offset, // contextMenu[x], closeBox[x], contextMenu[y], closeBox[y]
                refX1, refY1, refX2, refY2;

            // position close box and color selector
            if( this.isDiagonal ) {
                // Diagonal Line: position closebox to upper right of rightmost end point of line
                let             endCircle1 = this.getLineAdornment( refLineAdorns, lineIndex, 2 ),
                                endCircle2 = this.getLineAdornment( refLineAdorns, lineIndex, 3 );
                refX1 = endCircle1.attr("cx" );
                refX2 = endCircle2.attr("cx" );
                refY1 = endCircle1.attr("cy" );
                refY2 = endCircle2.attr("cy" );
                if( refX2 > refX1 ) {
                    refX1 = refX2;
                    refY1 = refY2;
                }
                offset = [ 0.85, 1.85, 0, 0 ].map( timesIconSize );
                // Note: this.setViewMode() also hides these elements, maybe do it in one place only? -CDM 2022-03-10
                Dyn.plotUtil.showOrHideElement( endCircle1, wantVisible );
                Dyn.plotUtil.showOrHideElement( endCircle2, wantVisible );
            } else if( this.isHorizontal ) {
                // on X axis: offset buttons to right of top of vertical line
                refX1 = lineElement.attr("path")[0][1];
                refY1 = lineElement.attr("path")[0][2];
                offset = [ -0.7, -1.7, 0.3, 0.3 ].map( timesIconSize );
            } else if( this.isOnY2 ){
                // on Y2 axis: offset buttons below the left end of the vertical line
                refX1 = lineElement.attr("path")[0][1];
                refY1 = lineElement.attr("path")[0][2];
                offset = [ 0.3, 0.3, 0.7, 1.7 ].map( timesIconSize );
            } else {
                // on Y/Y1 axis: offset buttons below the right end of the vertical line
                refX1 = lineElement.attr("path")[1][1];
                refY1 = lineElement.attr("path")[1][2];
                offset = [ -0.3, -0.3, 0.7, 1.7 ].map( timesIconSize );
            }
            Dyn.assertFiniteNumeric( refX1 ); // objects exist and have valid coords?
            Dyn.assertFiniteNumeric( refY1 );

            closeBox.attr( 'clip-rect', clipRectString );
            closeBox.transform( "t" + (refX1 + offset[1]) + "," + (refY1 + offset[3]) );
            Dyn.plotUtil.showOrHideElement( closeBox, wantVisible );

            contextMenu.attr( 'clip-rect', clipRectString );
            contextMenu.transform( "t" + (refX1 + offset[0]) + "," + (refY1 + offset[2]) );
            Dyn.plotUtil.showOrHideElement( contextMenu, wantVisible );
        } else {
            // hide adornments that are shown during selection
            if( closeBox && Dyn.plotUtil.isElementVisible( closeBox )) {
                closeBox.hide();
                contextMenu.hide();
            }
        }
    }

    /**
     * Add a measure to display in the Summary View, as part of the "movable line" table.
     * @param axisName
     * @param lineIndex
     * @param valueString
     * @param lineColor
     */
    addToMeasures( /*rowPrefix,*/ axisName, lineIndex, valueString, lineColor ) {

        let plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            lineID = this.getLineID( lineIndex );
        //Dyn.assert( lineID ===(rowPrefix + (lineIndex+1)) );

        function addOneMeasure( name, value ) {
            let oneMeasure = { setID:"referenceLine", measureName: name, value: value, lineID: lineID };
            plotMeasures.addToMeasuresArray( measuresArray, oneMeasure );
        }

        addOneMeasure( Dyn.loc("Axis"), axisName );
        addOneMeasure( Dyn.loc("Location"), valueString );
        addOneMeasure( plotMeasures.getNameOfColorMeasure(), lineColor );
    }

    clearOldMeasures() {
        let plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType );
        measuresArray.length = 0; // remove old measures (values for summary table)
    }

    getLineID( lineIndex ) {
        let whichPrefix = (
            this.isHorizontal ? 0 : // on X-axis
            ((!this.hasNumericY2Axis) ? 1 : // on Y-axis (Dual Axis OFF)
                ((!this.isOnY2) ? 2 : 3 ))); // on Y1 or Y2 axis (Dual Axis ON)
        return (['X-','Y-','Y1-','Y2-'][whichPrefix])  + (lineIndex+1);
    }

    /**
     * Update measures after adjusting a line position or color.
     * To be used only when the full plot calculation is not needed.
     */
    updateMeasureValues() {
        this.calcAdornments();
        this.plotShaper.measures.publishUpdates();
    }

    /**
     * Add hover and touch handlers for the given line element.
     * @param lineElement
     * @param coverElement
     * @param otherElements (optional array, for diagRefLine only )
     */
    addHoverTouchHandlers( lineElement, coverElement, otherElements ) {
        Dyn.assert( lineElement );
        Dyn.assert( coverElement );
        var plotElement = this;

        function hoverColorChange( lineIndex, isHoverIn ) {
            if( plotElement.isDiagonal ){
                let lineModel = plotElement.lines[ lineIndex ],
                    color = isHoverIn ?
                        Dyn.PlotLabelsUtil.getHoverColorFromLabelColor( lineModel.color ) :
                        lineModel.color;
                const isTemporaryChange = true;
                plotElement.setLineElementColor( lineIndex, color, isTemporaryChange );
            }
        }
        function hoverIn( e ) {
            this.data("hover",true);
            plotElement.prepareForDragEvents( true );
            if( this.data("tooltip") ) {
                Dyn.toolTip.show( this, e, {/*x: this.attrs.x, y: this.attrs.y*/} );
            }
            let lineIndex = lineElement.data("lineIndex"),
                lineModel = plotElement.lines[ lineIndex ],
                glowElements = plotElement.getGlowElements( lineElement, lineIndex );
            hoverColorChange( lineIndex, true );
            plotElement.addGlow( glowElements, lineModel.color, Dyn.plotUtil.lineGlowWidth );
        }

        function hoverOut( e ) {
            this.data("hover",false);
            if( ! lineElement.data("drag")) {
                // only if neither dragging nor touching
                if ( Dyn.toolTip.isMyElement( lineElement )) {
                    Dyn.toolTip.hide();
                }
                let lineIndex = lineElement.data("lineIndex");
                hoverColorChange( lineIndex, false );
                plotElement.removeGlow();
                plotElement.prepareForDragEvents( false );
            }
        }

        // NOTE: touch handling is not needed, because all elements have drag handlers
        // that work on touch devices, so dragStart() can handle the mouse/finger down
        // action on all devices.
        // We can save this working code for possible future enhancements.
        //function touchIn( e ) {
        //    glowStart( plotElement, this );
        //}
        //function touchOut( e ) {
        //    this.glowAndTipEnd( plotElement, this );
        //}
        //
        //coverElement.touchstart( touchIn.bind( lineElement ));
        //coverElement.touchend( touchOut.bind( lineElement ));

        if( ! Dyn.environment.isTouchDevice()) {
            // handle hover over the invisible line cover
            coverElement.hover( hoverIn.bind( lineElement ), hoverOut.bind( lineElement ) );
            this.setDragCursor( coverElement, null );

            if( otherElements ) {
                // handle hover over the end circles and Stat label of a movable line
                otherElements.forEach( el => {
                    el.hover( hoverIn, hoverOut );
                    this.setDragCursor( el, null );
                });
            }
        }
    }

    /**
     *  Add a keydown handler for the given reference line element (and its invisible cover, if non-null)
     */
    addKeyHandlers( visibleElement, optionalCoverElement, dragStart, dragMove, dragEnd ) {

        function keyDownHandler( e ) {
            //console.log( "keyDownHandler keydown:", e.key );
            switch( e.key ) { // const {key} = event; ES6+
            case "Delete":
            case "Backspace":
                // compare to clickedOnClosebox()
                plotElement.removeLineAndAdornment( this.data( "lineIndex" ) );
                break;
            case "Enter":
                plotElement.setGlowAndSelection( this );
                break;
            case "ArrowLeft":
            case "ArrowRight":
            case "ArrowUp":
            case "ArrowDown": {   // move this element one pixel in arrow's direction
                    let dist = Dyn.plotUtil.arrowKeyAcceleratedDistance( e.key ),
                        x, y /* placeholders -- x,y not used by drag handlers */,
                        element = this;
                    dragStart.call( element, x, y, e );
                    dragMove.call( element, dist.dx, dist.dy, x, y, e );
                    dragEnd.call( element, e );
                }
                break;
            }
        }

        var plotElement = this,
            elementToFocus = optionalCoverElement || visibleElement;
        Dyn.plotUtil.setKeyboardFocusable( elementToFocus, keyDownHandler.bind( visibleElement ), Dyn.loc("Reference Line"));
    }

    getGlowElements( oneElement, lineIndex ) {
        // glow only the lineElement for X, Y Ref Line, or Movable Line
        let refLineAdorns = this.getAdornmentsArray();
        return this.getLineAdornment( refLineAdorns, lineIndex, 0 );
    }

    /**
     * Select the given line (for click or touch) and also glow it in case there is no hover.
     * @param oneElement - one element of an X or Y or Movable line (not necessarily the visible line element)
     */
    setGlowAndSelection( oneElement ) {
        Dyn.assert( oneElement && isFinite( oneElement.data( "lineIndex" ) ) );
        var lineIndex = oneElement.data( "lineIndex" ),
            lineModel = this.lines[lineIndex];

        this.selectAdornment( lineIndex );
        this.updateSelectionVisuals(); // update the close box position

         // update the glow position
        let glowElements = this.getGlowElements( oneElement, lineIndex ); // for X or Y Reference Line
        this.addGlow( glowElements, lineModel.color, Dyn.plotUtil.lineGlowWidth );
    }

    setDragCursor( lineElement, event ) {
        var cursor;
        switch( this.primaryType ) {
        case Dyn.EPlotControl.kControlsXRefLine:
            cursor = Dyn.plotUtil.cursorHorizontalBothSide;
            break;
        case Dyn.EPlotControl.kControlsYRefLine:
        case Dyn.EPlotControl.kControlsY2RefLine:
            cursor = Dyn.plotUtil.cursorVerticalBothSide;
            break;
        case Dyn.EPlotControl.kControlsDiagRefLine:
        case Dyn.EPlotControl.kControlsDiagRefLineY2:
        default:
            cursor = Dyn.plotUtil.cursorMove;
            break;
        }
        if( lineElement ) {
            // attribute method for future mouse-over events
            lineElement.attr( "cursor", cursor );
        } else if( event ) {
            // immediate method for when dragging is not over the line
            event.currentTarget.style.cursor=cursor;
        }
    }

    /**
     * Get a valid axis view to match this line, for Dual Axis plots
     * If line is on Y2 axis and Y2 axis exists, return y2AxisView;
     * If line is on Y2 axis and Y2 axis is null, return yAxisView;
     * If line is on Y/Y1 axis, return yAxisView.
     * @param yAxisView
     * @returns {*}
     */
    getAxisOrSecondaryAxis( yAxisView ) {
        return ( this.isOnY2 && yAxisView.secondAxis ) ? yAxisView.secondAxis : yAxisView;
    }

    /**
     * Tell plot elements to hide or show any controls.
     */
    setViewMode( mode ){
        // NOTE: buttons may be null, that is OK
        let showControls = mode.showControls(),
            refLineAdorns = this.getAdornmentsArray(),
            closeBox = this.getCloseBox( refLineAdorns ),
            contextMenu = this._getContextMenuButton( refLineAdorns );

        Dyn.plotUtil.showOrHideElement( closeBox, showControls );
        Dyn.plotUtil.showOrHideElement( contextMenu, showControls );
        if( this.isDiagonal ) {
            this.lines.forEach( (lineModel, lineIndex) => {
                // Diagonal Line: position closebox to upper right of rightmost end point of line
                let endCircle1 = this.getLineAdornment( refLineAdorns, lineIndex, 2 ),
                    endCircle2 = this.getLineAdornment( refLineAdorns, lineIndex, 3 );
                Dyn.plotUtil.showOrHideElement( endCircle1, showControls );
                Dyn.plotUtil.showOrHideElement( endCircle2, showControls );
            });
        }
    }

    /**
     * Update the dual axis state for references lines on Y, there are 3 combinations:
     *  this.isOnY2 - either true or false depending on which derived object we are using e.g. YRefLineElement or Y2RefLineElement
     *  this.isDualAxisOn - either true or false, user can turn off Dual axis preference after Y2 lines are created, so we must hide them.
     *  this.hasNumericY2Axis - either true or false, depending on what attribute is on the Y2 axis
     */
    updateDualAxisState( yAxisView ) {
        // note: this.isOnY2 is a constant, either true or false depending on which derived object we are using e.g. YRefLineElement or Y2RefLineElement.
        this.isDualAxisOn = yAxisView.isFirstOfDualAxis(); // or Dyn.preferences.wantY2Axis();
        this.hasNumericY2Axis = yAxisView.isFirstOfDualAxis() ? yAxisView.secondAxis.isNumericalActiveAxis() : false;
    }

    /** start dragging a line, including setup of undo/redo */
    _dragLineStart() {
        this.dragUndoData = this.save(); // save all line data including line position and color
        Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to update selection in Summary View
    }

    /** finish dragging a line, including save of an undo/redo step */
    _dragLineEnd( lineIndex, wasDragged ) {
        Dyn.assert( this.dragUndoData );
        if( wasDragged ) {
            let restoreData = this.save();
            restoreData.draggedLineIndex = lineIndex; // for canUpdateCallBack below
            Dyn.mediator.publish( "plot:updateAdornment", {
                saveData: this.dragUndoData, // to create an undo step
                restoreData: restoreData,
                actionString: Dyn.loc( "Move {{action}}", { action: this.getUndoAction() } ),
                updatePlot: true, // to update location in Summary View
                canUpdateCallBack:// update the last undo step only if dragging the same line
                    ( lastUndoData, newUndoData ) => {
                        return lastUndoData.restoreData && newUndoData.restoreData
                            && (lastUndoData.restoreData.primaryType === newUndoData.restoreData.primaryType)
                            && (lastUndoData.actionString === newUndoData.actionString)
                            && (lastUndoData.restoreData.draggedLineIndex === newUndoData.restoreData.draggedLineIndex);
                    }
            } );
        }
        this.dragUndoData = null;
    }

    get kLineColors() {
        return Dyn.plotUtil.refLineColors;
    }
}
Dyn.RefLineBaseElement = RefLineBaseElement;
//Dyn.RefLineBaseElement.inheritsFrom( Dyn.PlotElement );
Dyn.RefLineBaseElement.prototype.kNumInitialAdornments = 2; // the close box and color selector, which get moved when line is selected, hidden with no selection
Dyn.RefLineBaseElement.prototype.kNumAdornmentsPerLine = 3; // 1 visible line, plus text for X and Y RefLines, plus invisible cover rect for click/drag; others should override
Dyn.RefLineBaseElement.prototype.isHorizontal = false;  // derived object XRefLineElement overrides to show it makes a horizontal line
Dyn.RefLineBaseElement.prototype.isDiagonal = false;    // derived object DiagRefLineElement overrides to show it makes a diagonal line
// Dyn.RefLineBaseElement.prototype.kLineColors = Dyn.plotUtil.refLineColors; // DiagRefLineElement overrides

//============================

/**
 * Dyn.XRefLineElement()
 * Reference line on the X-axis
 */
class XRefLineElement extends RefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityXRefLine,
            replaceType: Dyn.EPlotControl.kControlsXRefLine,
            primaryType: Dyn.EPlotControl.kControlsXRefLine,
            otherControls: [
                Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                Dyn.EPlotControl.kControlsRefLine ] // for fast add/remove since icons/margins don't change
        }, Dyn );
        this.lines = []; // array of line locations
        this.axisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.selectedLineIndex = -1; // index into this.lines or -1 if nothing selected
        this.addOneLine(); // note: maybe overriden by restore()
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef) {
        // update the X-axis attribute ID, and clear any old attribute coordinates
        //      This causes the lines to stay in the same relative view position (via normalized coordinates)
        //          even when the axis has completely different attribute coordinates.
        //      Otherwise the lines will stay fixed relative to the attribute coordinate position
        let result = Dyn.axisUtil.isNewAttributeCoord( this.axisAttributeID, subViewRef.xAxisView, this.isNewlyRestoredData );
        if( result.needToStripCoords ) {
            this.lines.forEach( function( item ){
                item.coord.stripAttributeCoord();
            });
        }
        this.axisAttributeID = result.newAttributeID;
        delete this.isNewlyRestoredData; // no longer newly restored data, if any (issue #555)
        this.handleDeferredSelectLine();
    }

    /**
    * Update adornments (specific to this element) to show a movable reference line on the X-axis of plot area
    */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
        const offsetWidth = 8,
            offsetHeight = 0;
        var refLineAdorns = ioAdornArray,
            plotElement = this,
            plotBounds = this.plotShaper.plotBounds,
            plotViewPaper = this.plotShaper.adornments.paper,
            leftBound = xAxisView.bounds.left(),
            rightBound = xAxisView.bounds.right(),
            newNumAdornments = this.kNumInitialAdornments +( this.lines.length * this.kNumAdornmentsPerLine ),
            createAdornments = ( refLineAdorns.length !== newNumAdornments ),
            viewOnly = Dyn.preferences.isViewOnly();

        this.axisAttributeID = xAxisView.isNumericalScaleAxis() ? xAxisView.getFormattingAttribute().id : "";
        this.clearOldMeasures();
        this.handleDeferredSelectLine();

        if( createAdornments ) {
            // remove old X-axis reference line(s) before rebuilding them
            Dyn.plotUtil.removeAndEmptyArrayOfElements( refLineAdorns );
            // add the "X" closeBox and square color selector for the selected line
            refLineAdorns.push( this.makeCloseBox().hide() );
            refLineAdorns.push( this.makeContextMenuButton().hide() );
        }

        // update or create reference lines
        for( let i=0; i<this.lines.length; ++i ) {
            updateRefLineElement( i, this.lines[i], createAdornments );
        }

        function dragStart( x, y, event ) {
            this.data( "sx", this.attr("path")[0][1] );
            plotElement.setGlowAndSelection( this ); // glow, select, show close box
            plotElement._dragLineStart();
        }

        function dragMove( dx, dy, x, y, e ) {
            // Note: dx and dy are positions relative to starting position (not last move)
            var xPosition = this.data("sx") + dx,
                //textX = xPosition + offsetWidth,
                lineIndex = this.data("lineIndex"),
                lineModel = plotElement.lines[ lineIndex ],
                snapToTickmarks = true,
                textString = "";

            // limit on it should not go beyond the boundary
            if( xPosition <= leftBound || xPosition >= rightBound) {
                return;
            }
            // update both view and normalized coordinates, line is positioned with or without numeric attribute on axis
            lineModel.coord.updateFromViewCoord( xPosition, xAxisView, snapToTickmarks, Dyn.plotUtil.refLineSnapDistance );
            xPosition = lineModel.coord.getViewCoord( xAxisView );
            let pathArray = this.attr("path" );
            pathArray[0][1] = xPosition;
            pathArray[1][1] = xPosition;
            this.attr("path",pathArray);
            pathArray = this.elementCover.attr("path");
            pathArray[0][1] = xPosition;
            pathArray[1][1] = xPosition;
            this.elementCover.attr("path",pathArray);
            this.data("drag",true);

            if( this.elementText ) {
                textString = stringValue( lineModel.coord.a, xAxisView );
                this.elementText.attr( "text", textString );
                let p = adjustedAxisPosition( this.elementText, xPosition );
                this.elementText.attr( { x:p.textX, y:p.textY });
                this.data( "tooltip", textString );
                if( textString.length && ! Dyn.environment.isTouchDevice()) {
                    if ( Dyn.toolTip.isMyElement( this ) )
                        Dyn.toolTip.update( this, e, {/*x: this.attrs.x, y: this.attrs.y*/} );
                    else if( ! e.keyCode )// change tip to this line if moved off element while dragging
                        Dyn.toolTip.show( this, e, {/*x: this.attrs.x, y: this.attrs.y*/} );
                    else
                        Dyn.toolTip.hide(); // hide tooltip if moving line with arrow keys
                }
            }

            plotElement.setGlowAndSelection( this ); // update glow, closebox, color positions
        }

        function adjustedAxisPosition( textElement, lineX ) {
            // if text is beyond right edge of plot, move to the left
            var textBBox = textElement.getBBox(),
                textX = lineX + offsetWidth,
                textY = plotBounds.y + offsetHeight + textBBox.height*0.4;
            if((textX + offsetWidth + textBBox.width) > rightBound ) {
                let iconSpacing = Dyn.plotUtil.plotButtonSpacing + Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.iconSize );
                textX = lineX - (textBBox.width + offsetWidth + (2 * iconSpacing));
            }
            return { textX:textX, textY:textY };
        }

        function stringValue( value, axisView ) {
            // round value to same precision as one pixel distance on axis
            if( axisView.isNumericalScaleAxis() ) {
                var attr          = axisView.getFormattingAttribute(),
                    onePixelScale = Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale ) ),
                    options = { precision:Dyn.preferences.getPrecision() }; // add user precision, if any
                value = Dyn.mathUtil.roundToScale( value, onePixelScale, options );
                return attr.axisValueToString( value, options.precision );
            } else {
                return "";
            }
        }

        function dragEnd() {
            let wasDragged = this.data("drag")===true;
            this.data("drag",false)
            if( ! this.data("hover")) {
                // only if neither dragging nor hovering
                plotElement.prepareForDragEvents( false );
            }
            plotElement._dragLineEnd( this.data("lineIndex"), wasDragged );
            plotElement.removeGlow();
            Dyn.plotUtil.setAsKeyboardFocus( this.elementCover );
        }

        function updateRefLineElement( lineIndex, lineModel, createAdornments ) {
            var sx         = lineModel.coord.getViewCoord( xAxisView ),
                sy         = plotBounds.y,
                ey         = plotBounds.y + plotBounds.height,
                textX      = sx + offsetWidth,
                textY      = sy + offsetHeight,
                pathString = "M" + sx + "," + sy + "L" + sx + "," + ey,
                textString = stringValue( lineModel.coord.a, xAxisView ),
                clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                isShowingLine = true, // always true on X axis, unlike Y or Movable line
                isDragableLine = ! viewOnly,
                isShowingLabel = (textString.length > 0) && isShowingLine, // for label and Summary View line
                lineElem;

            if( createAdornments ) {
                lineElem = plotViewPaper.path( pathString );
                lineElem.elementText = plotViewPaper.text( textX, textY, textString  );
                lineElem.elementCover = plotViewPaper.path( pathString );
                refLineAdorns.push( lineElem );
                refLineAdorns.push( lineElem.elementText );
                refLineAdorns.push( lineElem.elementCover );
                if( isDragableLine ) {
                    lineElem.elementCover.drag(dragMove.bind(lineElem), dragStart.bind(lineElem), dragEnd.bind(lineElem));
                    plotElement.addKeyHandlers(lineElem, lineElem.elementCover, dragStart, dragMove, dragEnd);
                }
                plotElement.addHoverTouchHandlers( lineElem, lineElem.elementCover );
            } else {
                lineElem = plotElement.getLineAdornment( refLineAdorns, lineIndex, 0 );
                lineElem.attr({ path:pathString });
                lineElem.elementText.attr({ x:textX, y:textY, text:textString });
                lineElem.elementCover.attr({ path:pathString });
            }
            Dyn.assert( lineElem === plotElement.getLineAdornment( refLineAdorns, lineIndex, 0 ));
            Dyn.assert( lineElem.elementText === plotElement.getLineAdornment( refLineAdorns, lineIndex, 1 ));
            Dyn.assert( lineElem.elementCover === plotElement.getLineAdornment( refLineAdorns, lineIndex, 2 ));
            lineElem
                .attr( {stroke: lineModel.color, "stroke-width": Dyn.plotUtil.refLineWidth, 'clip-rect':clipRectString } )
                .data( "lineIndex", lineIndex )
                .data( "tooltip", textString );

            // show the numeric value or hold place with empty string
            lineElem.elementText
                .attr( { "fill": lineModel.color, "text-anchor": "start",
                    "font-size":Dyn.preferences.getFontSize(), "font-family":Dyn.plotUtil.statLabelFontFamily,
                    'clip-rect':clipRectString } );
            let p = adjustedAxisPosition( lineElem.elementText, sx );
            lineElem.elementText.attr({ x:p.textX, y:p.textY });

            let w = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.refLineCoverWidth );
            lineElem.elementCover
                .attr( Dyn.debugViewRects ?
                        { fill:"none", "stroke-width": w, stroke: "orange", opacity:0.5 } : // make visible for debugging
                        { fill:"none", "stroke-width": w, stroke: "white", opacity:Dyn.plotUtil.opacityInvisibleButDraggable });

            if( lineIndex === plotElement.selectedLineIndex ) {
                plotElement.updateSelectionVisuals(); // to reposition close box
            }
            if( isShowingLabel ) {
                // show line location and color in Summary View if line is on a numeric axis
                plotElement.addToMeasures( /*'X-',*/ Dyn.loc("X"), lineIndex, textString, lineModel.color );
            }
            Dyn.plotUtil.showOrHideElement( lineElem, isShowingLine );
            Dyn.plotUtil.showOrHideElement( lineElem.elementText, isShowingLabel );
            Dyn.plotUtil.showOrHideElement( lineElem.elementCover, isDragableLine );
        }
    }

}
Dyn.XRefLineElement = XRefLineElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsXRefLine, Dyn.XRefLineElement );
Dyn.XRefLineElement.prototype.kAxisName = 'X';
Dyn.XRefLineElement.prototype.firstRefLineLocation = 0.55;
Dyn.XRefLineElement.prototype.nextRefLineLocation = 0.07;
Dyn.XRefLineElement.prototype.isHorizontal = true;  // makes horizontal lines

//============================

/**
 * Dyn.YRefLineBaseElement()
 * Base class for a reference line on the Y or Y2 axis.
 */
class YRefLineBaseElement extends RefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef) {
        "use strict";

        // update the Y-axis attribute ID, and clear any old attribute coordinates
        //      This causes the lines to stay in the same relative view position (via normalized coordinates)
        //          even when the axis has completely different attribute coordinates.
        //      Otherwise the lines will stay fixed relative to the attribute coordinate position
        //      In the special case of a Y2 reference line, and then Y2 axis removed, line will switch to the Y axis attribute;
        //          then if the Y2 axis is re-created, Y2 line will switch back to the Y2 axis attribute.
        let axisView1or2 = this.getAxisOrSecondaryAxis( subViewRef.yAxisView );
        let result = Dyn.axisUtil.isNewAttributeCoord( this.axisAttributeID, axisView1or2, this.isNewlyRestoredData );
        if( result.needToStripCoords ) {
            this.lines.forEach( function( item ){
                item.coord.stripAttributeCoord();
            });
        }
        this.axisAttributeID = result.newAttributeID;
        delete this.isNewlyRestoredData; // no longer newly restored data (issue #555)

        this.updateDualAxisState( subViewRef.yAxisView );
        this.handleDeferredSelectLine();
    }

    /**
     * Update adornments (specific to this element) to show a movable reference line on the X-axis of plot area
     */
    calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView, noAnimation ) {
        "use strict";

        //const dualAxisLineOffset = 20; // shorten reference lines on a dual axis so they stay away from the other axis
        let axisView1or2 = this.getAxisOrSecondaryAxis( yAxisView ),
            refLineAdorns = ioAdornArray,
            plotElement = this,
            plotViewPaper = this.plotShaper.adornments.paper,
            //offsetWidth = 0,
            offsetHeight = 12,
            topBound = yAxisView.bounds.top(),
            bottomBound = yAxisView.bounds.bottom(),
            leftBound = xAxisView.bounds.left(),
            rightBound = xAxisView.bounds.right(),
            newNumAdornments = this.kNumInitialAdornments +( this.lines.length * this.kNumAdornmentsPerLine ),
            createAdornments = ( refLineAdorns.length !== newNumAdornments ),
            viewOnly = Dyn.preferences.isViewOnly();

        // if dual axis plot, move Y1 lines away from Y2 axis, and move Y2 lines away from Y1 axis
        this.updateDualAxisState( yAxisView );
        if( this.isDualAxisOn ) {
            Dyn.assert( this.isDualAxisOn );
            if( this.isOnY2 ) { // change line endpoint, with minimum length of 1 pixel
                leftBound = Math.min( leftBound + Dyn.plotUtil.refLineDualAxisOffset, rightBound - 1 );
            } else {
                rightBound = Math.max( rightBound - Dyn.plotUtil.refLineDualAxisOffset, leftBound + 1 );
            }
        }

        this.axisAttributeID = axisView1or2.isNumericalScaleAxis() ? axisView1or2.getFormattingAttribute().id : "";
        this.clearOldMeasures();
        this.handleDeferredSelectLine();

        if( createAdornments ) {
            // remove old Y-axis reference line(s) before rebuilding them
            Dyn.plotUtil.removeAndEmptyArrayOfElements( refLineAdorns );
            // add the "X" closeBox and square color selector for the selected line
            refLineAdorns.push( this.makeCloseBox().hide() );
            refLineAdorns.push( this.makeContextMenuButton().hide() );
        }

        // update or create reference lines
        for( let i=0; i<this.lines.length; ++i ) {
            updateRefLineElement( i, this.lines[i], createAdornments, axisView1or2 /*, this.isOnY2, this.hasNumericY2Axis*/ );
        }

        function dragStart( x, y, event ) {
            this.data( "sx", this.attr("path")[0][1] );
            this.data( "sy", this.attr("path")[0][2] );
            plotElement.setGlowAndSelection( this );
            plotElement._dragLineStart();
        }

        function dragMove( dx, dy, x, y, e ) {

            // Note: dx and dy are positions relative to starting position (not last move)
            var yPosition = this.data("sy") + dy,
                textX = this.data("sx"),
                lineIndex = this.data("lineIndex" ),
                lineModel = plotElement.lines[ lineIndex ],
                snapToTickmarks = true,
                textString = "",
                axisView1or2 = plotElement.getAxisOrSecondaryAxis( yAxisView ); // yAxisView is stable; Y2 is not!

            // limit on it should not go beyond the boundary
            if(yPosition >= bottomBound || yPosition <= topBound) {
                return;
            }
            // update both view and normalized coordinates, line is positioned with or without numeric attribute on axis
            lineModel.coord.updateFromViewCoord( yPosition, axisView1or2, snapToTickmarks, Dyn.plotUtil.refLineSnapDistance );
            yPosition = lineModel.coord.getViewCoord( axisView1or2 );
            let pathArray = this.attr("path" );
            pathArray[0][2] = yPosition;
            pathArray[1][2] = yPosition;
            this.attr("path",pathArray);
            pathArray = this.elementCover.attr("path");
            pathArray[0][2] = yPosition;
            pathArray[1][2] = yPosition;
            this.elementCover.attr("path",pathArray);
            this.data("drag",true);

            if( this.elementText ) {
                textString = stringValue( lineModel.coord.a, axisView1or2 );
                textX = plotElement.isOnY2 ? leftBound : rightBound;
                this.elementText.attr({ text:textString, y:(yPosition - offsetHeight), x:textX });
                this.data( "tooltip", textString );
                if( textString.length && ! Dyn.environment.isTouchDevice()) {
                    if ( Dyn.toolTip.isMyElement( this ) )
                        Dyn.toolTip.update( this, e, {/*x: this.attrs.x, y: this.attrs.y*/} );
                    else // change tip to this line if moved off element while dragging
                        Dyn.toolTip.show( this, e, {/*x: this.attrs.x, y: this.attrs.y*/} );
                }
            }

            plotElement.setGlowAndSelection( this ); // update glow, closebox, color positions
        }

        // function adjustedAxisPosition( textX, textBBox ) {
        //     // both Y1 and Y2 text is right-justified on right edge of line.
        //     return textX;
        // }

        function stringValue( value, axisView ) {
            // round value to same precision as one pixel distance on axis
            if( axisView.isNumericalScaleAxis() ) {
                var attr          = axisView.getFormattingAttribute(),
                    onePixelScale = Math.abs( Dyn.mathUtil.invertSafely( axisView.coordMap.scale ) ),
                    options = { precision:Dyn.preferences.getPrecision() }; // add user precision, if any
                value = Dyn.mathUtil.roundToScale( value, onePixelScale, options );
                return attr.axisValueToString( value, options.precision );
            } else {
                return "";
            }
        }

        function dragEnd() {
            let wasDragged = this.data("drag")===true;
            this.data("drag",false);
            if( ! this.data("hover")) {
                // only if neither dragging nor hovering
                plotElement.prepareForDragEvents( false );
            }
            plotElement._dragLineEnd( this.data("lineIndex"), wasDragged );
            plotElement.removeGlow();
            Dyn.plotUtil.setAsKeyboardFocus( this.elementCover );
        }

        function updateRefLineElement( lineIndex, lineModel, createAdornments, axisView1or2 ) {
            var sx = leftBound,
                sy = lineModel.coord.getViewCoord( axisView1or2 ),
                ex = rightBound,
                textX = plotElement.isOnY2 ? leftBound : rightBound,
                textY = sy - offsetHeight,
                pathString = "M" + sx + "," + sy + "L" + ex + "," + sy,
                textString = stringValue( lineModel.coord.a, axisView1or2 ),
                clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
                isShowingLine = (plotElement.isOnY2 ? plotElement.isDualAxisOn : true ), // hide lines on Y2 if Dual Axis off
                isDragableLine = ! viewOnly,
                isShowingLabel = (textString.length > 0) && isShowingLine, // for label and Summary View line
                lineElem;

            if( createAdornments ) {
                lineElem = plotViewPaper.path( pathString );
                lineElem.elementText = plotViewPaper.text( textX, textY, textString  );
                lineElem.elementCover = plotViewPaper.path( pathString );
                refLineAdorns.push( lineElem );
                refLineAdorns.push( lineElem.elementText );
                refLineAdorns.push( lineElem.elementCover );
                if( isDragableLine ){
                    lineElem.elementCover.drag(dragMove.bind(lineElem), dragStart.bind(lineElem), dragEnd.bind(lineElem));
                    plotElement.addKeyHandlers( lineElem, lineElem.elementCover, dragStart, dragMove, dragEnd );
                }
                plotElement.addHoverTouchHandlers( lineElem, lineElem.elementCover );
            } else {
                lineElem = plotElement.getLineAdornment( refLineAdorns, lineIndex, 0 );
                lineElem.attr({ path:pathString });
                lineElem.elementText.attr({ x:textX, y:textY, text:textString });
                lineElem.elementCover.attr({ path:pathString });
            }
            Dyn.assert( lineElem === plotElement.getLineAdornment( refLineAdorns, lineIndex, 0 ));
            Dyn.assert( lineElem.elementText === plotElement.getLineAdornment( refLineAdorns, lineIndex, 1 ));
            Dyn.assert( lineElem.elementCover === plotElement.getLineAdornment( refLineAdorns, lineIndex, 2 ));
            lineElem
                .attr( {stroke: lineModel.color, "stroke-width": Dyn.plotUtil.refLineWidth, 'clip-rect':clipRectString } )
                .data( "lineIndex", lineIndex )
                .data( "tooltip", textString );

            // show the numeric value or hold place with empty string
            lineElem.elementText
                .attr( { "fill": lineModel.color, "text-anchor": (plotElement.isOnY2 ?"start":"end"),
                        "font-size":Dyn.preferences.getFontSize(), "font-family":Dyn.plotUtil.statLabelFontFamily,
                        'clip-rect':clipRectString } );
            // textX = adjustedAxisPosition( textX, lineElem.elementText.getBBox());
            // lineElem.elementText.attr( "x", textX );

            let w = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.refLineCoverWidth );
            lineElem.elementCover
                .attr( Dyn.debugViewRects ?
                    { fill:"none", "stroke-width": w, stroke: "orange", opacity:0.5 } : // make visible for debugging
                    { fill:"none", "stroke-width": w, stroke: "white", opacity:Dyn.plotUtil.opacityInvisibleButDraggable });

            if( lineIndex === plotElement.selectedLineIndex ) {
                plotElement.updateSelectionVisuals(); // to reposition close box
            }
            if( isShowingLabel ) {
                // show line location and color in Summary View if line is on a numeric axis
                let whichAxis = plotElement.isOnY2 ? 2 : (plotElement.hasNumericY2Axis? 1 : 0),
                    //rowPrefix = ["Y-", "Y1-", "Y2-"][whichAxis],
                    axisName = Dyn.loc(["Y", "Y1", "Y2"][whichAxis]);
                plotElement.addToMeasures( /*rowPrefix,*/ axisName, lineIndex, textString, lineModel.color );
            }
            Dyn.plotUtil.showOrHideElement( lineElem, isShowingLine );
            Dyn.plotUtil.showOrHideElement( lineElem.elementText, isShowingLabel );
            Dyn.plotUtil.showOrHideElement( lineElem.elementCover, isDragableLine );
        }
    };
}
Dyn.YRefLineBaseElement = YRefLineBaseElement;

//============================

/**
 * Dyn.YRefLineVElement()
 * Reference line on the Y axis.
 */
class YRefLineElement extends YRefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityYRefLine,
            replaceType: Dyn.EPlotControl.kControlsYRefLine,
            primaryType: Dyn.EPlotControl.kControlsYRefLine,
            otherControls: [
                Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                Dyn.EPlotControl.kControlsRefLine ] // for fast add/remove since icons/margins don't change
        }, Dyn );
        this.lines = []; // array of line locations
        this.axisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.selectedLineIndex = -1; // index into this.lines or -1 if nothing selected
        this.addOneLine(); // note: maybe overriden by restore()
    }
}
Dyn.YRefLineElement = YRefLineElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsYRefLine, Dyn.YRefLineElement );
Dyn.YRefLineElement.prototype.kAxisName = 'Y';
Dyn.YRefLineElement.prototype.firstRefLineLocation = 0.55;
Dyn.YRefLineElement.prototype.nextRefLineLocation = 0.07;

//============================

/**
 * Dyn.Y2RefLineElement()
 * Reference line on the Y2 axis of a Dual-axis plot.
 */
class Y2RefLineElement extends YRefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityYRefLine, // Y and Y2 have same priority: first line created is positioned on bottom of z-axis
            replaceType: Dyn.EPlotControl.kControlsY2RefLine,
            primaryType: Dyn.EPlotControl.kControlsY2RefLine,
            otherControls: [
                Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                Dyn.EPlotControl.kControlsRefLine ] // for fast add/remove since icons/margins don't change
        }, Dyn );
        this.lines = []; // array of line locations
        this.axisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.selectedLineIndex = -1; // index into this.lines or -1 if nothing selected
        this.addOneLine(); // note: maybe overriden by restore()
    }

    get isOnY2() { return true; } /* OVERRIDE */
}
Dyn.Y2RefLineElement = Y2RefLineElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsY2RefLine, Dyn.Y2RefLineElement );
Dyn.Y2RefLineElement.prototype.kAxisName = 'Y2';
Dyn.Y2RefLineElement.prototype.firstRefLineLocation = 0.65; // different from YRefLineElement.firstRefLineLocation to avoid overlap
Dyn.Y2RefLineElement.prototype.nextRefLineLocation = 0.07;

//============================

/**
 * model for a single diagonal reference Line (controlled by DiagRefLineElement, which can have 1+ diagonal lines)
 */
class DiagRefLineModel {
    /**
     * @param lineColor -- SVG color
     * @param lineIndex -- which line, so new lines can be offset from default position
     * @param isOnY2
     */
    constructor( lineColor, lineIndex, isOnY2 ) {
        // displayName can be used to determine object type
        this.displayName = "DiagRefLineModel";
        this.color = lineColor;
        // each coordinate will be defined in three coordinate spaces (n, a, v)
        //  this.x1.n for [0.0-1.0] normalized coordinates
        //  this.x1.a for attribute coordinates (e.g. to keep line at 20 for weight attribute on axis)
        //  this.x1.v for view coordinates (e.g. to map .n of 0.5 to view coordinate 200)
        // we start with normalized coordiantes and add others as needed, see NACoord.getViewCoord(), updateFromViewCoord()
        this.x1 = new Dyn.axisUtil.NACoord( 0.2 );
        this.y1 = new Dyn.axisUtil.NACoord( 0.2 );
        this.x2 = new Dyn.axisUtil.NACoord( 0.8 );
        this.y2 = new Dyn.axisUtil.NACoord( 0.8 );
        // offset from line to user-dragged label
        this.ox = NaN;
        this.oy = NaN;

        // offset from first line
        let yOffset = (isOnY2 ? (-1 - lineIndex) : lineIndex) * DiagRefLineModel.kNextLineOffset; // Y2 lines in negative direction
        this.y1.n -= yOffset;
        this.y2.n += yOffset;
        // wrap around if outside of [0-1] range so new lines don't overlap old
        this.y1.n = (this.y1.n - Math.floor( this.y1.n )) % 1;
        this.y2.n = (this.y2.n - Math.floor( this.y2.n )) % 1;
    }

    static kNextLineOffset = 0.03;

    save() {
        var data = {};
        data.normal = {
            // Note: unlike RefLineModel.save(), we are not saving .a coordinate, which may result in some loss of precision.
            //  If we change this we also need to handle restoring of data saved in old format.
            x1: this.x1.n,
            y1: this.y1.n,
            x2: this.x2.n,
            y2: this.y2.n,
        };
        if( Number.isFinite( this.ox ) ) {
            data.normal.ox = this.ox;
        }
        if( Number.isFinite( this.oy ) ) {
            data.normal.oy = this.oy;
        }
        data.color = this.color;
        return data;
    }

    restore( data ) {
        if( data.normal ) {
            if( Number.isFinite( data.normal.x1 ) ) {
                this.x1 = new Dyn.axisUtil.NACoord( data.normal.x1 );
            }
            if( Number.isFinite( data.normal.y1 ) ) {
                this.y1 = new Dyn.axisUtil.NACoord( data.normal.y1 );
            }
            if( Number.isFinite( data.normal.x2 ) ) {
                this.x2 = new Dyn.axisUtil.NACoord( data.normal.x2 );
            }
            if( Number.isFinite( data.normal.y2 ) ) {
                this.y2 = new Dyn.axisUtil.NACoord( data.normal.y2 );
            }
            if( Number.isFinite( data.normal.ox ) ) {
                this.ox = data.normal.ox;
            }
            if( Number.isFinite( data.normal.oy ) ) {
                this.oy = data.normal.oy;
            }
        }
        if( data.color ) {
            this.color = data.color;
        }
    }

    /** save label position coordinates for restore, if any */
    labelPositionToModel( textElement ) {
        let labelOffset = textElement.data( "offset" );
        if( labelOffset ) {
            // save offset from widget to user-dragged label
            this.ox = labelOffset.x.n;
            this.oy = labelOffset.y.n;
            //console.log( "DiagRefLineModel::labelPositionToModel", this.ox, this.oy );
        }
    }

    /** restore any normalized positions that were saved, else do nothing */
    modelToLabelPosition( textElement, cellRect ) {
        let labelOffset = textElement.data( "offset" ) || {};
        if( Number.isFinite( this.ox ) ) {
            // restore the relative offset from widget to user-dragged label,
            //  which is used by PlotLabelsUtil.restoreLabelPositionAndOffset()
            labelOffset.x = new Dyn.axisUtil.NACoord( this.ox );
            labelOffset.y = new Dyn.axisUtil.NACoord( this.oy );
            textElement.data( "offset", labelOffset );
            textElement.data( "cellRect", cellRect );
            //console.log("DiagRefLineModel::modelToLabelPosition", this.normalizedCoords);
        }
    }

} // class DiagRefLineModel
Dyn.DiagRefLineModel = DiagRefLineModel;


//============================

/**
 * Dyn.DiagRefLineBaseElement()
 *
 * A draggable line segment with small circles at the ends, can be diagonal, horizontal, vertical.
 * Shows the line equation if X and Y are numberlines.
 *
 * KNOWN AS: "Movable Line on X-Y" to users, rename to MovableLineElement ?
 */
class DiagRefLineBaseElement extends RefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
        this.showSquaresLineIndex = -1; // index into this.getAdornmentsArray() or -1 if no lines showing squares
    }

// modify the control type in this plot element, to turn on and off features
toggleControl( toggleControlID ) {
    "use strict";

    // instead of toggling anything just add another reference line
    this.addOneLine();
}

/** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
calcAxis( ioWhatChanged, subViewRef) {
    "use strict";

    // update the X- or Y-axis attribute ID, and clear any old attribute coordinates
    //      This causes the lines to stay in the same relative view position (via normalized coordinates)
    //          even when the axis has completely different attribute coordinates.
    //      Otherwise the lines will stay fixed relative to the attribute coordinate position
    //      In the special case of a Y2 reference line, and then Y2 axis removed, line will switch to the Y axis attribute;
    //          then if the Y2 axis is re-created, Y2 line will switch back to the Y2 axis attribute.
    {
        let yAxisView1or2 = this.getAxisOrSecondaryAxis( subViewRef.yAxisView );
        let xData = Dyn.axisUtil.isNewAttributeCoord( this.xAxisAttributeID, subViewRef.xAxisView, this.isNewlyRestoredData ),
            yData = Dyn.axisUtil.isNewAttributeCoord( this.yAxisAttributeID, yAxisView1or2, this.isNewlyRestoredData );

        this.lines.forEach( function( item ) {
            if( xData.needToStripCoords ){
                item.x1.stripAttributeCoord();
                item.x2.stripAttributeCoord();
            }
            if( yData.needToStripCoords ){
                item.y1.stripAttributeCoord();
                item.y2.stripAttributeCoord();
            }
        } );
        this.xAxisAttributeID = xData.newAttributeID;
        this.yAxisAttributeID = yData.newAttributeID;
        delete this.isNewlyRestoredData; // no longer newly restored data
    }

    this.updateDualAxisState( subViewRef.yAxisView );

    // finish selecting the line, so we have a newly selected line if added on initialization,
    // selection may also change which object shows Squares (Diagonal and Least Squares lines),
    // so we need to do this before each is updated by calcAdornments())
    this.handleDeferredSelectLine();
}

/**
 * constrain the line endpoints to be within the plot boundaries
 * @param lineModel {Dyn.DiagRefLineModel}
 */
clipNormalCoords( lineModel ) {
	"use strict";
	// NOTE: with the introduction of NACoords to allow line to move with axis adjustments,
    // this clipping has no effect on any numeric numberline axis. It affects non-numeric
    // axes only. This seems OK for now. We may or may not want to drop clipping entirely --CDM 2017-11-06
    lineModel.x1.n = Dyn.mathUtil.clipToRange( lineModel.x1.n, 0, 1 );
    lineModel.y1.n = Dyn.mathUtil.clipToRange( lineModel.y1.n, 0, 1 );
    lineModel.x2.n = Dyn.mathUtil.clipToRange( lineModel.x2.n, 0, 1 );
    lineModel.y2.n = Dyn.mathUtil.clipToRange( lineModel.y2.n, 0, 1 );
}

updateElementsFromNormalCoords( lineIndex, refLineAdorns, xAxisView, yAxisView ) {
    let yAxisView1or2 = this.getAxisOrSecondaryAxis( yAxisView );
    let lineElement = this.getLineAdornment(  refLineAdorns, lineIndex, 0 ),
        coverElement = this.getLineAdornment( refLineAdorns, lineIndex, 1 ),
        end1Element = this.getLineAdornment(  refLineAdorns, lineIndex, 2 ),
        end2Element = this.getLineAdornment(  refLineAdorns, lineIndex, 3 ),
        textRect = this.getLineAdornment(  refLineAdorns, lineIndex, 4 ),
        connectorLine = this.getLineAdornment(  refLineAdorns, lineIndex, 5 ),
        textElement = this.getLineAdornment(  refLineAdorns, lineIndex, 6 ),
        squaresSet = this.getLineAdornment(  refLineAdorns, lineIndex, 7 ),
        lineModel = this.lines[ lineIndex ],
        wantEquation = (xAxisView.isNumberLineAxis() && yAxisView1or2.isNumberLineAxis()),
        isShowingLine = (this.isOnY2 ? this.isDualAxisOn : true ), // hide lines on Y2 if Dual Axis off
        isShowingLabels = isShowingLine && wantEquation && this.plotShaper.showLabelsOnStats,
        isShowingControls = isShowingLine && ! Dyn.preferences.isViewOnly(),
        clipRectString = Dyn.plotView.getPlotInteriorClipRect();

    // update the 3 fixed elements from the normalized coordinates
    // NOTE: we save the view coordinates as a cache for dragging only, do not save with plot
    lineModel.x1.v = lineModel.x1.getViewCoord( xAxisView );
    lineModel.y1.v = lineModel.y1.getViewCoord( yAxisView1or2 );
    lineModel.x2.v = lineModel.x2.getViewCoord( xAxisView );
    lineModel.y2.v = lineModel.y2.getViewCoord( yAxisView1or2 );

    // update adornments
    lineElement.attr({ path: (
            "M" + lineModel.x1.v + "," + lineModel.y1.v +
            "L" + lineModel.x2.v + "," + lineModel.y2.v), 'clip-rect':clipRectString });
    coverElement.attr({ path: (
            "M" + lineModel.x1.v + "," + lineModel.y1.v +
            "L" + lineModel.x2.v + "," + lineModel.y2.v), 'clip-rect':clipRectString });
    end1Element.attr({ cx: lineModel.x1.v, cy: lineModel.y1.v, 'clip-rect':clipRectString });
    end2Element.attr({ cx: lineModel.x2.v, cy: lineModel.y2.v, 'clip-rect':clipRectString });


    if( lineIndex === this.selectedLineIndex ) {
        this.updateSelectionVisuals(); // to reposition close box
    }

    // show or hide label and connector line
    Dyn.plotUtil.showOrHideElement( textRect,       isShowingLabels );
    Dyn.plotUtil.showOrHideElement( connectorLine,  isShowingLabels );
    Dyn.plotUtil.showOrHideElement( textElement,    isShowingLabels );
    // hide line if on Y2 and Dual Axis is off
    Dyn.plotUtil.showOrHideElement( lineElement,    isShowingLine );
    Dyn.plotUtil.showOrHideElement( coverElement,   isShowingLine );
    Dyn.plotUtil.showOrHideElement( end1Element,    isShowingControls );
    Dyn.plotUtil.showOrHideElement( end1Element,    isShowingControls );

    //this.hasNumericY2Axis = Boolean( Dyn.plotView.y2Axis ? Dyn.plotView.y2Axis.isNumericalActiveAxis() : false );

    // update tooltip text
    let tipText = "",
        labelText = "";
    if ( wantEquation && isShowingLine ){
        let x1 = lineModel.x1.a,
            y1 = lineModel.y1.a,
            x2 = lineModel.x2.a,
            y2 = lineModel.y2.a,
            xAttr = xAxisView.getFormattingAttribute(),
            yAttr = yAxisView1or2.getFormattingAttribute(),
            equation = Dyn.plotUtil.findLineByTwoPoints( x1, y1, x2, y2 ),
            whichAxis = (this.isOnY2 ? 2:(this.hasNumericY2Axis ? 1 : 0)),
            filterParams = { dataSet:Dyn.dataSet },
            canCalcSquares = Number.isFinite(equation.slope),
            drawSquares = canCalcSquares && (lineIndex === this.showSquaresLineIndex),
            showSumOfSquares = canCalcSquares && this.plotShaper.showSquaresOnLines, // show stat 'sums of squares'?
            squaresModel = new Dyn.SquaresOnLineModel(
                xAxisView, yAxisView1or2, 0, 0, equation, filterParams, squaresSet, lineModel.color );
            function caseFilter( params, caseIndex, xValue, yValue ) {
                // return true of case should be included in least squares/sum of squares/show squares
                return( params.dataSet.isCaseNonMissingNonFiltered( caseIndex ) && // exclude filtered and missing cases (determined by dot plot)
                    Dyn.mathUtil.isFinite( xValue ) &&  // exclude non-numeric X values
                    Dyn.mathUtil.isFinite( yValue )     // exclude non-numeric Y values
                );
            }
            squaresModel.updateSquares( drawSquares, caseFilter, this, lineElement );

            let textParams = {
                y: yAttr.name,
                x: xAttr.name,
                m: "",
                b: "",
                yAxis: ["y","y1","y2"][whichAxis]
            };
        if( ! Dyn.mathUtil.isFinite( equation.slope )) {
            // vertical line if slope undefined
            textParams.x1 = xAttr.axisValueToString( x1 );
            tipText = Dyn.loc( "{{x}} = {{x1}}", textParams );
            labelText = Dyn.loc( "x = {{x1}}", textParams );
            textParams.x = textParams.x1; // x={value} for Summary View
            textParams.y = "";
        } else if( Dyn.mathUtil.isClose( equation.slope, 0, Dyn.mathUtil.kSmallerTolerance )) {
            // horizontal line if zero slope
            textParams.y1 = yAttr.axisValueToString( y1 );
            tipText = Dyn.loc( "{{y}} = {{y1}}", textParams );
            labelText = Dyn.loc( "{{yAxis}} = {{y1}}", textParams );
            //textParams.m = "0"; // zero slope for Summary View
            textParams.y = textParams.y1; // y={value} for Summary View
            textParams.x = "";
        } else {
            // diagonal line
            let decimals = new Dyn.mathUtil.slopeInterceptDecimalPlaces( xAttr, yAttr );
            textParams.m = decimals.slopeString( equation.slope, yAttr );
            textParams.interceptNotAbs = decimals.interceptNotAbs( equation.intercept, yAttr );
            if( Dyn.mathUtil.isClose( equation.intercept, 0, Dyn.mathUtil.kSmallerTolerance )) {
                // diagonal line with zero intercept
                tipText              = Dyn.loc( "{{y}} = ({{m}}) x", textParams );
                labelText            = Dyn.loc( "{{yAxis}} = ({{m}}) x", textParams );
                textParams.b         = "0"; // zero intercept for Summary View
            } else {
                // diagonal line with non-zero intercept
                textParams.b         = decimals.absInterceptString( equation.intercept, yAttr );
                textParams.plusMinus = decimals.interceptPlusMinusString( equation.intercept );
                tipText = Dyn.loc( "{{y}} = ({{m}}) {{x}} {{plusMinus}} {{b}}", textParams );
                labelText = Dyn.loc( "{{yAxis}} = ({{m}}) x {{plusMinus}} {{b}}", textParams );
            }
        }
        if( showSumOfSquares ) {
            let ss = Dyn.plotUtil.calcSumOfSquares( xAttr, yAttr, caseFilter, filterParams, equation );
            textParams.sumOfSquares = yAttr.axisValueToString( ss );
            tipText += '\n' + Dyn.loc("sum of squares = {{sumOfSquares}}", textParams );
            //labelText += '\n' + Dyn.loc("ss = {{sumOfSquares}}", textParams );
        }
        this.addToMeasures( textParams, lineIndex, lineModel.color );
    } else {
        Dyn.SquaresOnLineModel.removeSquares( squaresSet ); // if equation goes from valid to invalid
    }
    lineElement.data( "tooltip", tipText );
    end1Element.data( "tooltip", tipText );
    end2Element.data( "tooltip", tipText );
    textElement.attr({ text:labelText, "font-size":Dyn.preferences.getFontSize() }); // so setLabelPositionFirstPass() has the current text size

    if( isShowingLabels ) {
        let cell = this.plotShaper.plotBounds, // diagonal rect uses entire plot bounds, ignoring cells
            viewPt1 = { x:lineModel.x1.v, y:lineModel.y1.v },
            viewPt2 = { x:lineModel.x2.v, y:lineModel.y2.v },
            widgetRect = (new Dyn.mathUtil.Rect()).setFromTwoPoints( viewPt1, viewPt2 ),
            wantHorizontalConnectorLine = Dyn.PlotLabelsUtil.shouldConnectorLineBeHorizontal( cell, widgetRect, viewPt1, viewPt2 );
        lineModel.modelToLabelPosition( textElement, cell );
        let labelRect = Dyn.PlotLabelsUtil.setLabelPositionFirstPass( this, textElement, wantHorizontalConnectorLine, cell, widgetRect ),
            connectorLineRect = Dyn.PlotLabelsUtil.getConnectorLineFromLabelRectToDiagonalLine( labelRect, widgetRect, wantHorizontalConnectorLine, viewPt1, viewPt2 );
        textRect.attr({ x:labelRect.x, y:labelRect.y, width:labelRect.width, height:labelRect.height,
            'clip-rect':clipRectString });
        textElement
            .data("viewPt1", viewPt1)
            .data("viewPt2", viewPt2)
            .attr({ x:labelRect.centerX(), y:labelRect.centerY(), 'clip-rect':clipRectString });
        connectorLine.attr({ path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ),
            'clip-rect':clipRectString });
        this.plotShaper.setAdornmentLayer( 1, textRect, textElement, connectorLine );
    }
}

addLineElement( lineIndex, refLineAdorns, xAxisView, yAxisView ) {
    let yAxisView1or2 = this.getAxisOrSecondaryAxis( yAxisView );

	function dragStart( x, y, event ) {
		// save the starting position for dragMove()
        var lineIndex = this.data("lineIndex" ),
            lineModel = plotElement.lines[ lineIndex ];

		switch( this.data("which")) {
		case "end1":
			this.data( "sx", lineModel.x1.v );
			this.data( "sy", lineModel.y1.v );
			break;
		case "end2":
			this.data( "sx", lineModel.x2.v );
			this.data( "sy", lineModel.y2.v );
			break;
		case "line":
			this.data( "sx1", lineModel.x1.v );
			this.data( "sx2", lineModel.x2.v );
			this.data( "sy1", lineModel.y1.v );
			this.data( "sy2", lineModel.y2.v );
			break;
        case "label":
            this.data("sx",textElement.attr("x"));
            this.data("sy",textElement.attr("y"));
            this.attr("cursor", Dyn.plotUtil.cursorMove);
            break;
		}
        showOrUpdateToolTip( this, event ); // not shown until now for touch devices with no hover
        plotElement.setGlowAndSelection( this ); // update glow, closebox, color positions
        plotElement._dragLineStart();
	}

	function dragMove( dx, dy, x, y, e ) {
		// Note: dx and dy are positions relative to starting position (not last dragMove)
		// we need to update both the element position and save the normalized coordinates
        var lineIndex = this.data("lineIndex" ),
            lineModel = plotElement.lines[ lineIndex ],
            xView, yView;

		switch( this.data("which")) {
		case "end1":
			// move x1,y1 endpoint, snap to origin or x2 or y2 if close to make vertical or horizontal line
            xView = snapToPixel( this.data( "sx" ) + dx, [xAxisView.coordMap.transform( 0 ), lineModel.x2.v]);
            yView = snapToPixel( this.data( "sy" ) + dy, [yAxisView1or2.coordMap.transform( 0 ), lineModel.y2.v]);
            lineModel.x1.updateFromViewCoord( xView, xAxisView );
            lineModel.y1.updateFromViewCoord( yView, yAxisView1or2 );
			//plotElement.clipNormalCoords( lineModel );
            plotElement.updateElementsFromNormalCoords( lineIndex, refLineAdorns, xAxisView, yAxisView1or2 );
			break;
		case "end2":
			// move x2,y2 endpoint, snap to origin or x1 or y1 if close to make vertical or horizontal line
            xView = snapToPixel( this.data( "sx" ) + dx, [xAxisView.coordMap.transform( 0 ), lineModel.x1.v]);
            yView = snapToPixel( this.data( "sy" ) + dy, [yAxisView1or2.coordMap.transform( 0 ), lineModel.y1.v]);
            lineModel.x2.updateFromViewCoord( xView, xAxisView );
            lineModel.y2.updateFromViewCoord( yView, yAxisView1or2 );
			//plotElement.clipNormalCoords( lineModel );
            plotElement.updateElementsFromNormalCoords( lineIndex, refLineAdorns, xAxisView, yAxisView1or2 );
			break;
		case "line":
			// shift line, no snapping.
            lineModel.x1.updateFromViewCoord( this.data( "sx1" ) + dx, xAxisView );
            lineModel.x2.updateFromViewCoord( this.data( "sx2" ) + dx, xAxisView );
            lineModel.y1.updateFromViewCoord( this.data( "sy1" ) + dy, yAxisView1or2 );
            lineModel.y2.updateFromViewCoord( this.data( "sy2" ) + dy, yAxisView1or2 );
			//plotElement.clipNormalCoords( lineModel );
            plotElement.updateElementsFromNormalCoords( lineIndex, refLineAdorns, xAxisView, yAxisView1or2 );
			break;
        case "label": {
            let textRect         = plotElement.getLineAdornment( refLineAdorns, lineIndex, 4),
                connectorElement = plotElement.getLineAdornment( refLineAdorns, lineIndex, 5),
                textElement      = plotElement.getLineAdornment( refLineAdorns, lineIndex, 6),
                isXAxis          = textElement.data( "isXAxis" ),
                widgetRect       = textElement.data( "widgetRect" ),
                viewPt1          = textElement.data( "viewPt1" ),
                viewPt2          = textElement.data( "viewPt2" );
            // update the text element
            textElement.attr( { x: this.data( "sx" ) + dx, y: this.data( "sy" ) + dy } );
            // update the connector line and text background elements
            let labelRect         = Dyn.PlotLabelsUtil.getLabelRectFromTextElement( textElement ),
                connectorLineRect = Dyn.PlotLabelsUtil.getConnectorLineFromLabelRectToDiagonalLine( labelRect, widgetRect, isXAxis, viewPt1, viewPt2 );
            Dyn.PlotLabelsUtil.saveLabelPositionAndOffset( textElement, labelRect, widgetRect );
            lineModel.labelPositionToModel( textElement );
            textRect.attr( { x: labelRect.x, y: labelRect.y, width: labelRect.width, height: labelRect.height } );
            connectorElement.attr( { path: Dyn.PlotLabelsUtil.makeConnectorLinePathString( connectorLineRect ) } );
            //plotElement.setGlowAndSelection( this ); // update glow, closebox, color positions
            } break;
		}
		showOrUpdateToolTip( this, e );
		this.data("drag",true);
        plotElement.setGlowAndSelection( this ); // update glow, closebox, color positions
        if( this.data("which") !== "label" ) {
            Dyn.mediator.publish( "plot:updateAdornment", { adornmentSelected:true } ); // to update selection in Summary View
        }
	}

	function dragEnd() {
		if( ! this.data("hover")) {
			// only if neither dragging nor hovering
			plotElement.removeGlow();
			if ( Dyn.toolTip.isMyElement( this )) {
				Dyn.toolTip.hide();
			}
            plotElement.prepareForDragEvents( false );
        }
        let wasDragged = this.data("drag")===true;
		this.data("drag",false);
		let which = this.data("which"),
            isLine = (which==="line"),
            isLabel = (which==="label");
		if( !isLabel ) {
            plotElement._dragLineEnd( this.data("lineIndex"), wasDragged );
        }
        let elementToFocus = isLine ? this.coverElement : this; // cover or end circles, not line
        Dyn.plotUtil.setAsKeyboardFocus( elementToFocus );
	}

	function showOrUpdateToolTip( lineElement, event ) {
        // if updateElementsFromNormalCoords() set equation for a tooltip, display it
        if( lineElement.data("tooltip") ) {
            let options = {};
            if( Dyn.environment.isTouchDevice()) {
                options.alignCenterX = options.alignBottom = true;
            }
            if ( Dyn.toolTip.isMyElement( lineElement ) ) {
                Dyn.toolTip.update( lineElement, event, options );
            } else { // change tip to this line if moved off element while dragging
                Dyn.toolTip.show( lineElement, event, options );
            }
        }
    }

	/**
	 * if given coordinate is within x units of any coordinates in snapCoordsArray,
     * return the snap coordinate to snap to an exact coordinate value.
	 * @param {number} coord
	 * @param {array} snapCoordsArray
	 * @returns {number}
	 */
	function snapToPixel( coord, snapCoordsArray ) {
	    var firstSnapCoord = snapCoordsArray.find( function( snapCoord ) {
                // return first snapCoord close to given coordinate
                return Dyn.mathUtil.isClose( coord, snapCoord, Dyn.plotUtil.movableLineSnapDist );
            });
        return( isFinite( firstSnapCoord ) ? firstSnapCoord : coord ); // if no snapping, return original coordinate
	}

    let plotElement = this,
        plotViewPaper = this.plotShaper.adornments.paper,
        lineElement = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 0 ),
        coverElement = plotElement.getLineAdornment( refLineAdorns, lineIndex, 1 ),
        end1Element = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 2 ),
        end2Element = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 3 ),
        textRect = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 4 ),
        connectorLine = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 5 ),
        textElement = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 6 ),
        //squares = plotElement.getLineAdornment(  refLineAdorns, lineIndex, 7 ),
        //lineModel = plotElement.lines[ lineIndex ],
        lineColor = plotElement.lines[ lineIndex ].color,
        circleRadius = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.movableLineEndpointRadius ),
        viewOnly = Dyn.preferences.isViewOnly();

    // element 0: the actual line
    lineElement = plotViewPaper.path( "M0,0" ).
        attr( { stroke: lineColor, "stroke-width": Dyn.plotUtil.refLineWidth, "stroke-linecap":"round" } ).
        data("which","line" ).
        data("lineIndex",lineIndex);
    refLineAdorns.push( lineElement );

    // element 1:cover path for wider click area
    let w = Dyn.plotUtil.getTouchAdjustedSize( Dyn.plotUtil.refLineCoverWidth );
    coverElement = plotViewPaper.path( "M0,0" )
        .attr( Dyn.debugViewRects ?
            { fill:"none", "stroke-width": w, stroke: "orange", opacity:0.5 } : // make visible for debugging
            { fill:"none", "stroke-width": w, stroke: "white", opacity:Dyn.plotUtil.opacityInvisibleButDraggable });
    if( !viewOnly ) {
        coverElement.drag(dragMove.bind(lineElement), dragStart.bind(lineElement), dragEnd.bind(lineElement));
    }
    lineElement.coverElement = coverElement;
    refLineAdorns.push( coverElement );

    // element 2: draggable circle for 1st line endpoint
    end1Element = plotViewPaper.circle( 0, 0, circleRadius ).
        attr( {
            stroke:			lineColor, //Dyn.plotUtil.movableLineEndpointStroke,
            "stroke-width": Dyn.plotUtil.movableLineEndpointWidth,
            fill:			"white"
        } ).
        data("which","end1").
        data("lineIndex",lineIndex);
    if( !viewOnly ){
        end1Element.drag( dragMove, dragStart, dragEnd );
    }
    refLineAdorns.push( end1Element );

    // element 3: clone circles for 2nd endpoint
    end2Element = end1Element.clone();
    end2Element.
        data("which","end2").
        data("lineIndex",lineIndex);
    if( !viewOnly ){
        end2Element.drag( dragMove, dragStart, dragEnd );
    }
    refLineAdorns.push( end2Element );

    // element 4-6 labels
    textRect = plotViewPaper.rect( 0,0,0,0, Dyn.plotUtil.statLabelRoundRectRadius )
        .data( "layerNum", 1 ) // for layerAdornments()
        .data( "which", "label")
        .data( "lineIndex", lineIndex)
        .drag( dragMove, dragStart, dragEnd ) // OK to view and drag in viewOnly mode
        .attr({ opacity: Dyn.plotUtil.statLabelOpacity, fill: lineColor, stroke:"none", cursor: Dyn.plotUtil.cursorMove })
    connectorLine = plotViewPaper.path( "M0,0" )
        .data( "layerNum", 1 ) // for layerAdornments()
        .attr( {stroke: lineColor, 'stroke-width': Dyn.plotView.statLabelConnectorLineWidth } );
    Dyn.plotUtil.disableMouseOverEvents( connectorLine );
    textElement = plotViewPaper.text( 0, 0, "" )
        .data( "layerNum", 1 ) // for layerAdornments()
        .attr({ fill: Dyn.plotUtil.statLabelTextColor, "text-anchor": "middle",
            "font-size":Dyn.preferences.getFontSize(), "font-family":Dyn.plotUtil.statLabelFontFamily });
        //.drag( dragMove, dragStart, dragEnd ) // OK to view and drag in viewOnly mode
        //.data("which","label")
        //.data("lineIndex",lineIndex);
    Dyn.plotUtil.disableMouseOverEvents( textElement );
    textElement.getNonOverlappingLabel = function(){ return this; }; // for findNonOverlappingLabelRectInCell()
    //textElement.getNonOverlappingWidgetRect = function() {...} -- not used for DiagRefLineElement;
    refLineAdorns.push( textRect );
    refLineAdorns.push( connectorLine );
    refLineAdorns.push( textElement );

    // show squares: create the set to hold the squares plus dashed extender line
    let squareSet = plotViewPaper.set();
    // 0th element of set is dashed line (paper.path()), 1st+ elements are the squares (paper.rect())
    squareSet.getDashedLine = function(){ return this[0]; };
    squareSet.getNthSquare = function(n){ return (n<(this.length-1))?this[n+1]:null; }
    squareSet.getNumSquares = function(){ return Math.max( 0, this.length-1 ); }
    refLineAdorns.push( squareSet );

    plotElement.addHoverTouchHandlers( lineElement, coverElement, [end1Element, end2Element, textRect] );
    if( viewOnly ) {
        end1Element.hide();
        end2Element.hide();
    } else {
        plotElement.addKeyHandlers(lineElement, coverElement, dragStart, dragMove, dragEnd);
        plotElement.addKeyHandlers(end1Element, null, dragStart, dragMove, dragEnd);
        plotElement.addKeyHandlers(end2Element, null, dragStart, dragMove, dragEnd);
        plotElement.addKeyHandlers(textRect, null, dragStart, dragMove, dragEnd);
    }
}

/** adornments (specific to this element) are diagonal lines movable (draggable) by the user */
calcAdornments( ioAdornArray, iconPositions, xAxisView, yAxisView /*, noAnimation*/ ) {
    let refLineAdorns = ioAdornArray,
        newNumAdornments = this.kNumInitialAdornments +( this.lines.length * this.kNumAdornmentsPerLine ),
        yAxisView1or2 = this.getAxisOrSecondaryAxis( yAxisView );

    this.clearOldMeasures();
    this.updateDualAxisState( yAxisView );

	// create the initial adornments shared by all diagonal lines
	if( refLineAdorns.length < this.kNumInitialAdornments ) {
        // remove old adornments if any (just in case)
        Dyn.plotUtil.removeAndEmptyArrayOfElements( refLineAdorns );

        // add the "X" closeBox and square color selector for the selected line
        refLineAdorns.push( this.makeCloseBox().hide() );
        refLineAdorns.push( this.makeContextMenuButton().hide() );
    }

    // create or update the adornments for each diagonal line
    this.lines.forEach( (lineModel, lineIndex) => {
        let lineElement = this.getLineAdornment(  refLineAdorns, lineIndex, 0 );
        if( ! lineElement ) {
            // create all the adorments for this line (this.kNumAdornmentsPerLine)
            this.addLineElement( lineIndex, refLineAdorns, xAxisView, yAxisView1or2 );
        }
        // (re-) position the adornments for this line
        this.updateElementsFromNormalCoords( lineIndex, refLineAdorns, xAxisView, yAxisView1or2 );
    });
    Dyn.assert( newNumAdornments <= refLineAdorns.length );

    // remove any excess adornment elements (e.g. if diagonal line was deleted)
    for( let oldNumAdornments=refLineAdorns.length; oldNumAdornments > newNumAdornments; --oldNumAdornments ) {
        if( oldNumAdornments > newNumAdornments ) {
            refLineAdorns[oldNumAdornments - 1].remove();
            refLineAdorns.length = refLineAdorns.length - 1;
        }
    }
    Dyn.assert( newNumAdornments === refLineAdorns.length );
}

/**
 * Add  one reference line location. calcAdornment() will build line elements to match.
 * updates this.lines[], this.selectedLineIndex
 * @private
 */
addOneLine() {
    "use strict";
    var lineIndex = this.lines.length,
        defaultColor = this.kLineColors[ this.isOnY2 ? 5 : 0 ],
        newLineCoords = new Dyn.DiagRefLineModel( defaultColor, lineIndex, this.isOnY2 );

    this.clipNormalCoords( newLineCoords );
    this.lines.push( newLineCoords );
    if( ! this.plotShaper ){
        this.deferredSelectLine = lineIndex; // select later with handleDeferredSelectLine()
    } else {
        this.selectAdornment( lineIndex );
    }
    // calcAdornments() will add line adornment and show the line as selected (with close box)
}

getLineAdornment( refLineAdorns, lineIndex, partIndex ) {
    "use strict";
    var adornmentIndex = this.kNumInitialAdornments +( lineIndex * this.kNumAdornmentsPerLine ) + (partIndex ? partIndex : 0);
    if( refLineAdorns.length >= adornmentIndex ) {
        return refLineAdorns[ adornmentIndex ];
    }
    return null;
}

getCloseBox( refLineAdorns ) {
    "use strict";
    if( refLineAdorns.length >= this.kNumInitialAdornments ) {
        return refLineAdorns[ 0 ];
    }
    return null;
}

setLineElementColor( lineIndex, newColor, isTemporaryChange ) {
    "use strict";
    var lineAdorns = this.getAdornmentsArray(),
        lineModel = this.lines[lineIndex];

    if( ! isTemporaryChange ){
        lineModel.color = newColor; // don't save the change, if change is just for hoverIn() etc.
    }
    // darker gray endpoints for gray line
    //let endPointColor = ( newColor === this.kLineColors[0] ) ? Dyn.plotUtil.movableLineEndpointStroke : newColor;
    this.getLineAdornment( lineAdorns, lineIndex, 0 ).attr( {stroke: newColor} ); // lineElement
    // Note: part index 1 is invisible cover (coverElement), do not change
    this.getLineAdornment( lineAdorns, lineIndex, 2 ).attr( {stroke: newColor} ); // end1Element
    this.getLineAdornment( lineAdorns, lineIndex, 3 ).attr( {stroke: newColor} ); // end2Element
    this.getLineAdornment( lineAdorns, lineIndex, 4 ).attr( {fill: newColor} ); // textRect
    this.getLineAdornment( lineAdorns, lineIndex, 5 ).attr( {stroke: newColor} ); // connectorLine
    // Note: part index 6 is white text (textElement), do not change
    let squaresSet = this.getLineAdornment( lineAdorns, lineIndex, 7 );
    if( squaresSet ) {
        squaresSet.forEach( rect => rect.attr( {stroke: newColor}));
    }
}

/**
 * Add a measure to display in the Summary View, as part of the "movable line" table.
 * @param textParams
 * @param lineIndex
 * @param lineColor
 */
addToMeasures( textParams, lineIndex, lineColor ) {
    let plotMeasures = this.plotShaper.measures,
        measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
        lineID = this.getLineID( lineIndex );

    function addOneMeasure( name, value ) {
        let oneMeasure = { setID:"movableLine", measureName: name, value: value, lineID: lineID };
        plotMeasures.addToMeasuresArray( measuresArray, oneMeasure );
    }

    addOneMeasure( Dyn.loc("Y"),         textParams.y );
    addOneMeasure( Dyn.loc("Slope"),     textParams.m );
    addOneMeasure( Dyn.loc("X"),         textParams.x );
    addOneMeasure( Dyn.loc("Intercept"), textParams.interceptNotAbs );
    if( textParams.sumOfSquares ) {
        addOneMeasure( Dyn.loc("Sum of Squares"), textParams.sumOfSquares );
    }
    addOneMeasure( plotMeasures.getNameOfColorMeasure(), lineColor );
}

    /**
     * @param wantToShowSquares {Boolean}
     */
    setShowSquares( wantToShowSquares ) {
        if( wantToShowSquares ) {
            // if want to show squares, indicate which of 1+ lines should show squares
            this.showSquaresLineIndex = ( this.selectedLineIndex >= 0 ) ? this.selectedLineIndex : 0;
        } else { // else turn off show squares
            this.showSquaresLineIndex = -1;
        }
    }

    /** Does this plot element have lines that can show squares? */
    canShowSquares( requireSelectedLine ) {
        Dyn.assert( this[Dyn.EPlotControl.kControlsShowSquares]);
        let numLines = this.lines.length;
        return( requireSelectedLine ?
            ((numLines > 0) && Dyn.mathUtil.isInIntegerRange( this.selectedLineIndex, 0, numLines)) :
            (numLines > 0));
    }

    getLineID( lineIndex ) {
        let whichPrefix = (this.isOnY2 ? 2:(this.hasNumericY2Axis ? 1 : 0));
        return (['X-Y-','X-Y1-','X-Y2-'][whichPrefix])  + (lineIndex+1);
    }
}
Dyn.DiagRefLineBaseElement = DiagRefLineBaseElement;
Dyn.DiagRefLineBaseElement.prototype.kNumAdornmentsPerLine = 8; // 1 line, 1 invisible cover, 2 end point circles, 3 elements for label
Dyn.DiagRefLineBaseElement.prototype.isDiagonal = true;
// Dyn.DiagRefLineBaseElement.prototype.kLineColors = Dyn.plotUtil.movableLineColors; // DiagRefLineElement overrides


//============================


/**
 * Dyn.DiagRefLineElement()
 *
 * A draggable line segment with small circles at the ends, can be diagonal, horizontal, vertical.
 * Shows the line equation if X and Y are numberlines.
 * KNOWN AS: "Movable Line on X-Y" to users, rename to MovableLineElement ?
 */
class DiagRefLineElement extends DiagRefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityDiagRefLine,
            replaceType: Dyn.EPlotControl.kControlsDiagRefLine,
            primaryType: Dyn.EPlotControl.kControlsDiagRefLine,
            otherControls: [
                Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                Dyn.EPlotControl.kControlsRefLine,
                Dyn.EPlotControl.kControlsShowSquares ]
        }, Dyn );
        // line endpoints in normalized [0.0-1.0] coordinates, SAVE with plot to restore
        // line endpoints in view coordinates for dragging
        // NOTE: we save the view coordinates as a cache for dragging only, do not save with plot
        this.lines = [];
        this.xAxisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.yAxisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.addOneLine();
    }

}
Dyn.DiagRefLineElement = DiagRefLineElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsDiagRefLine, Dyn.DiagRefLineElement );
Dyn.DiagRefLineElement.prototype.kAxisName = 'X - Y';
// Dyn.DiagRefLineElement.prototype.kLineColors = Dyn.plotUtil.movableLineColors; // DiagRefLineElement overrides


//============================


/**
 * Dyn.DiagRefLineElementY2()
 *
 * A draggable line segment with small circles at the ends, can be diagonal, horizontal, vertical.
 * Shows the line equation if X and Y2 are numberlines.
 * Uses the Y2 attribute of a Dual-axis Plot, if it exists.
 * KNOWN AS: "Movable Line on X - Y2" to users.
 */
class DiagRefLineElementY2 extends DiagRefLineBaseElement {

    constructor( Dyn ) {
        super( Dyn )

        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityDiagRefLine,
            replaceType: Dyn.EPlotControl.kControlsDiagRefLineY2,
            primaryType: Dyn.EPlotControl.kControlsDiagRefLineY2,
            otherControls: [
                Dyn.EPlotControl.kControlsAdornmentPositionOnly,
                Dyn.EPlotControl.kControlsRefLine,
                Dyn.EPlotControl.kControlsShowSquares ]
        }, Dyn );
        // line endpoints in normalized [0.0-1.0] coordinates, SAVE with plot to restore
        // line endpoints in view coordinates for dragging
        // NOTE: we save the view coordinates as a cache for dragging only, do not save with plot
        this.lines = [];
        this.xAxisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.yAxisAttributeID = "";  // if non-empty, track refline in the attribute's coordinates
        this.addOneLine();
    }

    get isOnY2() { return true; } /* OVERRIDE */
}
Dyn.DiagRefLineElementY2 = DiagRefLineElementY2;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsDiagRefLineY2, Dyn.DiagRefLineElementY2 );
Dyn.DiagRefLineElementY2.prototype.kAxisName = 'X - Y2';
// Dyn.DiagRefLineElementY2.prototype.kLineColors = Dyn.plotUtil.movableLineColorsY2; // DiagRefLineElementY2 overrides

}; // end of wrapDynRefLineElement
